import { useState } from 'react';
import {
  Modal,
  ModalDialog,
  ModalClose,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormLabel,
  FormHelperText,
  Input,
  Select,
  Option,
  Button,
  Stack,
  Typography,
  Box,
  List,
  ListItem,
  ListItemDecorator,
} from '@mui/joy';
import { useMutation } from '@apollo/client';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';

import { useFeedback } from './feedback/Service';
import { parseGraphQLError } from '../utils';
import { INVITE_TEAM_MEMBER } from '../gql/team.mutations';
import {
  TEAM_ROLE_LABELS,
  TEAM_ROLE_PERMISSIONS,
  PERMISSION_LABELS,
  TEAM_ROLE_DESCRIPTIONS,
} from '../constants';

const AVAILABLE_ROLES = ['admin', 'user', 'viewer'];

const TeamInviteModal = ({ open, onClose, clientId, onSuccess, isOwner }) => {
  const feedback = useFeedback();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('user');
  const [emailError, setEmailError] = useState('');

  const [inviteTeamMember, { loading }] = useMutation(INVITE_TEAM_MEMBER);

  // Roles that can be assigned
  const assignableRoles = isOwner ? AVAILABLE_ROLES : ['user', 'viewer'];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setEmailError('');

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    try {
      await inviteTeamMember({
        variables: { clientId, email: email.toLowerCase().trim(), role },
      });
      feedback.snackbar({ text: 'Invitation sent successfully', type: 'success' });
      setEmail('');
      setRole('user');
      onSuccess?.();
    } catch (err) {
      const errorMessage = parseGraphQLError(err);
      if (errorMessage.toLowerCase().includes('email')) {
        setEmailError(errorMessage);
      } else {
        feedback.snackbar({ text: errorMessage, type: 'error' });
      }
    }
  };

  const handleClose = () => {
    setEmail('');
    setRole('user');
    setEmailError('');
    onClose();
  };

  const selectedRolePermissions = TEAM_ROLE_PERMISSIONS[role] || [];
  const allPermissions = Object.keys(PERMISSION_LABELS).filter(p => p !== 'TRANSFER_OWNERSHIP');

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalDialog sx={{ width: '100%', maxWidth: 450 }}>
        <ModalClose />
        <DialogTitle>Invite Team Member</DialogTitle>
        <DialogContent>
          <form onSubmit={handleSubmit} id="invite-form">
            <Stack spacing={2} sx={{ mt: 1 }}>
              <FormControl error={!!emailError} required>
                <FormLabel>Email address</FormLabel>
                <Input
                  type="email"
                  placeholder="colleague@company.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setEmailError('');
                  }}
                  autoFocus
                />
                {emailError && <FormHelperText>{emailError}</FormHelperText>}
              </FormControl>

              <FormControl required>
                <FormLabel>Role</FormLabel>
                <Select
                  value={role}
                  onChange={(_, value) => setRole(value)}
                >
                  {assignableRoles.map((r) => (
                    <Option key={r} value={r}>
                      {TEAM_ROLE_LABELS[r]}
                    </Option>
                  ))}
                </Select>
                <FormHelperText>{TEAM_ROLE_DESCRIPTIONS[role]}</FormHelperText>
              </FormControl>

              {/* Permission preview */}
              <Box sx={{ mt: 1 }}>
                <Typography level="body-sm" fontWeight="md" sx={{ mb: 1 }}>
                  Role permissions:
                </Typography>
                <List size="sm" sx={{ '--ListItem-paddingY': '2px' }}>
                  {allPermissions.map((permission) => {
                    const hasPermission = selectedRolePermissions.includes(permission);
                    return (
                      <ListItem key={permission}>
                        <ListItemDecorator>
                          {hasPermission ? (
                            <CheckIcon color="success" fontSize="small" />
                          ) : (
                            <CloseIcon color="disabled" fontSize="small" />
                          )}
                        </ListItemDecorator>
                        <Typography
                          level="body-sm"
                          sx={{ color: hasPermission ? 'text.primary' : 'text.tertiary' }}
                        >
                          {PERMISSION_LABELS[permission]}
                        </Typography>
                      </ListItem>
                    );
                  })}
                </List>
              </Box>
            </Stack>
          </form>
        </DialogContent>
        <DialogActions>
          <Button variant="plain" color="neutral" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" form="invite-form" loading={loading}>
            Send Invitation
          </Button>
        </DialogActions>
      </ModalDialog>
    </Modal>
  );
};

export default TeamInviteModal;
